//
//  This directive is used to register elements for height calculations of inner element list
//  It can be used for multiple types: source, height, target, item
//  'source' - will register entire source element for TOP offset calculations
//  'height' - will register elements that will be used to calculate cart content's height
//  'target' - will register element - cart items list - and will listen to height updated
//  'item' - will register cart item for height calculations
//
import { Directive, AfterViewInit, OnInit, OnDestroy, ElementRef, Input, HostBinding, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';

import * as Services from '@shared/core/services';
import { ScrollService } from '@shared/core/directives/scroll/scroll.shared.service';

import { Subscription, fromEvent } from 'rxjs';
import { auditTime } from 'rxjs/operators';

@Directive({
    selector: '[directive-max-on-screen-height]',
})
export class MaxOnScreenHeightDirective implements AfterViewInit, OnInit, OnDestroy, OnChanges {
    private _mode: 'source' | 'height' | 'target' | 'item';
    private _init: boolean = false;
    private _elem: HTMLElement;

    private _heightUpdateListener$: Subscription;
    private _windowResizeListener$: Subscription;
    private _windowUpdateListener$: Subscription;

    @HostBinding('style.height.px') public height: string = null;
    @HostBinding('style.transition') public transition: string = null;
    @Input() public registerId: string;
    @Input() public subRegisterId: string;
    @Input() public parent: boolean = false; /* Instead of this element, get it's parent element */
    @Input() public disabledBelow: number = null;
    @Input() public directiveSubHeight: number = null;
    @Input() public maxHeight: number = null;
    @Input() public changes: any; /* Helper to check if element's dimensions have changed */
    @Input() public minHeight: number;


    constructor(
        private _elementRef: ElementRef,
        private _maxOnScreenHeightService: Services.MaxOnScreenHeightService,
        private _scrollService: ScrollService,
    ) { }

    ngOnInit(): void {
        this._elem = this._elementRef.nativeElement;
        if (!this._elem) return;

        if (this.registerId === null || this.registerId === undefined) return;

        const tempMode = this._elem.getAttribute('directive-max-on-screen-height');
        if (tempMode !== 'source' && tempMode !== 'height' && tempMode !== 'target' && tempMode !== 'item') {
            return console.warn(`Mode invalid or not defined (mode: ${tempMode}) in "directive-max-on-screen-height" for element`,
                this._elem, ' Please add one of the following values: source, height, target, item');
        }

        if (!this.registerId) {
            return console.warn(`"registerId" property not defined (mode: ${tempMode}) in "directive-max-on-screen-height" for element`,
                this._elem, ' This value should be unique string of a group');
        }

        if ((tempMode === 'item' || tempMode === 'height') && !this.subRegisterId) {
            return console.warn(`"subRegisterId" property not defined (mode: ${tempMode}) in "directive-max-on-screen-height" for element`,
                this._elem, ' This value should be unique string of this item');
        }

        if (this.parent) {
            this._elem = this._elem.parentElement;
        }
        this._mode = tempMode;
        this._init = true;
    }

    ngOnChanges(simpleChanges: SimpleChanges): void {
        if (this._init && this.registerId) {
            this._maxOnScreenHeightService.updateRequest(this.registerId);
        }
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            if (!this._init) return;
            switch (this._mode) {
                case 'source':
                    this._maxOnScreenHeightService.registerSource(this.registerId, this._elem);
                    break;

                case 'height':
                    this._maxOnScreenHeightService.registerHeightAdjustItem(this.registerId, this.subRegisterId, this._elem);
                    if (this.minHeight) this._setMinHeightListenerSubscription(this.subRegisterId);
                    break;

                case 'item':
                    this._maxOnScreenHeightService.registerItem(this.registerId, this.subRegisterId, this._elem);
                    break;

                case 'target':
                    this._maxOnScreenHeightService.registerTarget(this.registerId, this._elem);

                    this._windowResizeListener$ = this._setWindowEventListener('resize');
                    this._windowUpdateListener$ = this._setWindowEventListener('update');

                    this._setHeightListenerSubscription(this.registerId);
                    break;
            }
        }, 0);
    }

    ngOnDestroy(): void {
        if (!this._init) return;
        switch (this._mode) {
            case 'source':
                this._maxOnScreenHeightService.registerSource(this.registerId, null);
                break;

            case 'height':
                this._maxOnScreenHeightService.registerHeightAdjustItem(this.registerId, null);
                break;

            case 'item':
                this._maxOnScreenHeightService.registerItem(this.registerId, this.subRegisterId, null);
                break;

            case 'target':
                this._maxOnScreenHeightService.registerTarget(this.registerId, null);
                if (this._windowResizeListener$ && !this._windowResizeListener$.closed) this._windowResizeListener$.unsubscribe();
                if (this._windowUpdateListener$ && !this._windowUpdateListener$.closed) this._windowUpdateListener$.unsubscribe();
                if (this._heightUpdateListener$ && !this._heightUpdateListener$.closed) this._heightUpdateListener$.unsubscribe();
                break;
        }
    }

    private _setWindowEventListener(eventName: string): Subscription {
        return fromEvent(window, eventName)
            .pipe(
                auditTime(200)
            )
            .subscribe(() => {
                this._maxOnScreenHeightService.registerTarget(this.registerId, this._elem); /* Will update height when resizing */
            });
    }

    private _setHeightListenerSubscription(key: string): void {
        if (!this.disabledBelow || window.innerWidth >= this.disabledBelow) {
            this.height = '0';
            this.transition = 'height .2s ease';
        }
        setTimeout(() => {
            /* http://support.taskretail.com.au:8080/browse/TOLO-59 */

            this._heightUpdateListener$ = this._maxOnScreenHeightService.getHeightListener$(key)
                .subscribe(newHeight => {
                    if (this.disabledBelow && window.innerWidth < this.disabledBelow) {
                        this.height = null;
                        this.transition = null;
                    } else {
                        const subHeight = (this.directiveSubHeight) ? this.directiveSubHeight : 0;
                        this.height = this.maxHeight && newHeight > this.maxHeight ? `${this.maxHeight - subHeight}` : `${newHeight - subHeight}`;
                        this._scrollService.requestUpdate(); /* Make sure scroll position is updated! */
                    }
                });

            this._maxOnScreenHeightService.updateRequest(key);
        }, 201);
    }

    private _setMinHeightListenerSubscription(key: string): void {
        setTimeout(() => {
            this._maxOnScreenHeightService.getHeightListener$(key)
                .subscribe(newHeight => {
                    this.height =
                        this.maxHeight && newHeight > this.maxHeight ?
                            this.minHeight && newHeight < this.minHeight ?
                                `${this.minHeight}` : `${newHeight}` : `${this.maxHeight}`;

                    this._scrollService.requestUpdate(); /* Make sure scroll position is updated! */
                });

            this._maxOnScreenHeightService.updateRequest(key);
        }, 201);
    }
}
